import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle, ClockCounterClockwise, MagnifyingGlass, FileText, MapPinLine,
  XCircle, ArrowsClockwise, IdentificationBadge, CreditCard, ShieldCheck,
  Package, QrCode, CopySimple,
} from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { passengerApi } from "@/lib/passengerApi";
import { MajheHeader } from "@/components/majhe/MajheLayout";

const TIMELINE = [
  { key: "Submitted",            label: "Submitted",            icon: FileText,                blurb: "Application received by KTCL" },
  { key: "Under Review",         label: "Under Review",         icon: MagnifyingGlass,         blurb: "Depot officer is verifying documents" },
  { key: "Approved",             label: "Approved",             icon: CheckCircle,             blurb: "Documents accepted, preparing your physical copy" },
  { key: "Ready for Collection", label: "Ready for Collection", icon: MapPinLine,              blurb: "Show pickup code at the counter" },
  { key: "Collected",            label: "Collected",            icon: Package,                 blurb: "Card / pass handed over — happy travels!" },
];

const STATUS_ORDER = {
  "Submitted": 0, "Under Review": 1, "Approved": 2,
  "Ready for Collection": 3, "Collected": 4, "Rejected": 99,
};

export default function Applications() {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRef, setExpandedRef] = useState(null);
  const timerRef = useRef(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await passengerApi.get("/me/applications");
      setApps(data);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Poll every 10s so the timeline updates as soon as the admin moves the status.
    timerRef.current = setInterval(() => load(true), 10000);
    return () => clearInterval(timerRef.current);
  }, []);

  return (
    <div data-testid="majhe-applications">
      <MajheHeader title="My Applications" onBack={() => navigate("/majhe")}
        right={
          <button onClick={() => load()} data-testid="apps-refresh-btn"
            type="button" aria-label="Refresh applications"
            className="text-[#5B2EE5] p-2">
            <ArrowsClockwise size={18} weight="bold"/>
          </button>
        }/>

      {loading && <div className="px-5 grid gap-2 mt-2"><div className="majhe-skel h-32"/><div className="majhe-skel h-32"/></div>}

      {!loading && apps.length === 0 && (
        <div className="px-5 mt-2">
          <div className="majhe-card text-center py-10">
            <FileText size={42} className="mx-auto mb-3 opacity-80" aria-hidden="true"/>
            <div className="font-extrabold text-lg">No applications yet</div>
            <div className="text-sm opacity-85 mt-1 max-w-xs mx-auto">
              When you apply for a Smart Pass or Smart Transit Card, you'll be able to track it here.
            </div>
            <button onClick={() => navigate("/majhe/pass")} data-testid="apps-go-pass-btn"
              className="majhe-btn mt-5 majhe-btn--ghost" type="button">
              Apply for Smart Pass
            </button>
          </div>
        </div>
      )}

      {!loading && apps.length > 0 && (
        <div className="px-5 mt-1 space-y-3">
          {apps.map((a) => (
            <ApplicationCard key={`${a.kind}-${a.ref}`} app={a}
              expanded={expandedRef === a.ref}
              onToggle={() => setExpandedRef((cur) => cur === a.ref ? null : a.ref)}/>
          ))}
        </div>
      )}
    </div>
  );
}

function ApplicationCard({ app, expanded, onToggle }) {
  const Icon = app.kind === "pass" ? IdentificationBadge : CreditCard;
  const idx = STATUS_ORDER[app.status] ?? 0;
  const isRejected = app.status === "Rejected";

  return (
    <div className="bg-white rounded-2xl border border-[#EDE5FF] shadow-sm overflow-hidden"
      data-testid={`app-card-${app.ref}`}>
      <button onClick={onToggle} type="button"
        aria-expanded={expanded}
        aria-label={`${app.title} — currently ${app.status}. Tap to ${expanded ? "collapse" : "see"} timeline.`}
        className="w-full p-4 flex items-start gap-3 text-left">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: "var(--majhe-card-grad)", color: "white" }}>
          <Icon size={22} weight="bold"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2">
            <div className="text-[15px] font-extrabold text-[#18142A] truncate flex-1">{app.title}</div>
            <StatusPill status={app.status}/>
          </div>
          <div className="text-[11.5px] text-[#7B7390] mt-0.5 truncate">
            Ref: <span className="font-bold text-[#4920C2]">{app.ref}</span> · Collect at {app.depot}
          </div>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-5 pt-1" data-testid={`app-timeline-${app.ref}`}>
          {isRejected ? (
            <div className="bg-[#FEE2E2] border border-[#FBB4B4] rounded-xl p-3 mt-1">
              <div className="flex items-center gap-2 font-bold text-[#991B1B]">
                <XCircle size={16} weight="fill"/> Application Rejected
              </div>
              <div className="text-[11.5px] text-[#7C1212] mt-1">
                {app.review_notes || "Please re-apply with corrected documents."}
              </div>
            </div>
          ) : (
            <ol className="space-y-3 mt-2" aria-label="Application status timeline">
              {TIMELINE.map((step, i) => {
                const reached = i <= idx;
                const isCurrent = i === idx;
                const StepIcon = step.icon;
                return (
                  <li key={step.key} className="flex gap-3" data-testid={`timeline-step-${step.key.replace(/\s+/g, "-").toLowerCase()}`}>
                    <div className="flex flex-col items-center" aria-hidden="true">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-all
                        ${reached ? "bg-[#5B2EE5] text-white" : "bg-[#EDE5FF] text-[#9C8FCE]"}
                        ${isCurrent ? "ring-4 ring-[#DCC9FF]" : ""}`}>
                        <StepIcon size={16} weight={reached ? "fill" : "regular"}/>
                      </div>
                      {i < TIMELINE.length - 1 && (
                        <div className={`w-[3px] flex-1 mt-1 mb-1 rounded-full ${i < idx ? "bg-[#5B2EE5]" : "bg-[#EDE5FF]"}`}
                          style={{ minHeight: 22 }}/>
                      )}
                    </div>
                    <div className="flex-1 pb-2">
                      <div className={`text-[13.5px] font-bold ${reached ? "text-[#18142A]" : "text-[#9C8FCE]"}`}>
                        {step.label}
                      </div>
                      <div className="text-[11.5px] text-[#7B7390] leading-snug mt-0.5">{step.blurb}</div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}

          {/* Detailed history */}
          {app.review_history?.length > 0 && (
            <div className="mt-4 pt-3 border-t border-[#EDE5FF]">
              <div className="text-[10.5px] uppercase tracking-wider font-bold text-[#7B7390] mb-2">
                Activity log
              </div>
              <div className="space-y-2">
                {app.review_history.map((h, i) => (
                  <div key={i} className="flex items-start gap-2 text-[11.5px]">
                    <ClockCounterClockwise size={12} className="text-[#5B2EE5] mt-0.5 shrink-0" aria-hidden="true"/>
                    <div className="flex-1">
                      <div className="text-[#18142A] font-semibold">{h.to} <span className="text-[#7B7390] font-normal">· {h.by}</span></div>
                      {h.notes && <div className="text-[#7B7390]">{h.notes}</div>}
                      <div className="text-[10px] text-[#9C8FCE]">{new Date(h.at).toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pickup Code + QR — only when Ready for Collection */}
          {app.status === "Ready for Collection" && (
            <PickupTokenCard app={app}/>
          )}
        </div>
      )}
    </div>
  );
}

function PickupTokenCard({ app }) {
  const [tok, setTok] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQR, setShowQR] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await passengerApi.get(
          `/applications/${app.kind}/${app.id}/pickup-token`
        );
        if (!cancelled) setTok(data);
      } catch (e) {
        // 404 if not generated yet — silently fail; banner will show retry
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [app.id, app.kind]);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(tok.pickup_otp || "");
      toast.success("Pickup code copied");
    } catch { toast.error("Copy failed"); }
  };

  if (loading) {
    return <div className="majhe-skel h-40 mt-4"/>;
  }

  if (!tok) {
    return (
      <div className="bg-[#EDE5FF] border border-[#C9B5FF] rounded-xl p-3 mt-4">
        <div className="flex items-center gap-2 font-bold text-[#2D1170]">
          <MapPinLine size={16} weight="fill"/> Ready at {app.depot} Depot
        </div>
        <div className="text-[11.5px] text-[#4920C2] mt-1">
          Reference: <b>{app.ref}</b> · Carry your original documents.
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-2xl overflow-hidden border-2 border-violet-300 shadow-lg" data-testid={`pickup-token-${app.ref}`}>
      {/* Gradient header */}
      <div className="bg-gradient-to-br from-[#6F2EE8] via-[#4A1FB3] to-[#2D1170] text-white px-4 pt-4 pb-3">
        <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-wider font-bold opacity-90">
          <ShieldCheck size={12} weight="fill"/> Step 4 · Pickup Confirmation
        </div>
        <div className="text-base font-extrabold mt-0.5">
          Your card/pass is ready
        </div>
        <div className="text-[11.5px] opacity-90 mt-0.5">
          Show this code at <b>{tok.depot} KTC counter</b> to collect.
        </div>
      </div>

      {/* OTP digits */}
      <div className="bg-white px-4 py-4">
        <div className="text-[10.5px] uppercase tracking-wider text-[#7B7390] font-bold text-center mb-2">
          Pickup Code
        </div>
        <div className="flex items-center justify-center gap-1.5 mb-3" data-testid="pickup-otp-digits">
          {String(tok.pickup_otp || "------").split("").map((d, i) => (
            <div key={i} className="w-10 h-12 rounded-lg bg-[#F5F2FF] border-2 border-[#5B2EE5]
              flex items-center justify-center text-2xl font-mono font-extrabold text-[#2D1170] tabular-nums">
              {d}
            </div>
          ))}
        </div>
        <div className="flex items-center justify-center gap-2">
          <button onClick={copyCode} type="button" data-testid="copy-pickup-otp"
            className="flex items-center gap-1 text-[11px] font-bold text-[#5B2EE5] px-3 py-1.5 rounded-full bg-[#F5F2FF]">
            <CopySimple size={12} weight="bold"/> Copy code
          </button>
          <button onClick={() => setShowQR((v) => !v)} type="button" data-testid="toggle-pickup-qr"
            className="flex items-center gap-1 text-[11px] font-bold text-white px-3 py-1.5 rounded-full bg-[#5B2EE5]">
            <QrCode size={12} weight="bold"/> {showQR ? "Hide" : "Show"} QR
          </button>
        </div>
      </div>

      {/* QR (collapsible) */}
      {showQR && (
        <div className="bg-white px-4 pb-4 flex flex-col items-center" data-testid="pickup-qr-block">
          <div className="bg-white p-3 rounded-xl border border-[#EDE5FF] shadow-sm">
            <QRCodeSVG value={tok.pickup_token} size={180} level="H" includeMargin={false}/>
          </div>
          <div className="text-[11px] text-[#7B7390] mt-2 text-center max-w-xs">
            Counter staff can scan this with the KTCL QR Validator. Token expires once consumed.
          </div>
        </div>
      )}

      {/* Footer reference */}
      <div className="bg-[#FAF7FF] border-t border-[#EDE5FF] px-4 py-3 flex items-center justify-between text-[11px]">
        <div>
          <div className="text-[#7B7390] font-bold uppercase tracking-wider text-[10px]">Ref</div>
          <div className="text-[#2D1170] font-extrabold">{tok.ref}</div>
        </div>
        <div className="text-right">
          <div className="text-[#7B7390] font-bold uppercase tracking-wider text-[10px]">Holder</div>
          <div className="text-[#2D1170] font-extrabold">{tok.holder_name}</div>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    "Submitted":            { bg: "#EDE5FF", fg: "#5B2EE5" },
    "Under Review":         { bg: "#FFF3E0", fg: "#9A6308" },
    "Approved":             { bg: "#DCC9FF", fg: "#4920C2" },
    "Ready for Collection": { bg: "#2D1170", fg: "#FFFFFF" },
    "Collected":            { bg: "#D1FAE5", fg: "#065F46" },
    "Rejected":             { bg: "#FEE2E2", fg: "#991B1B" },
  };
  // Short label on phones so the pill never squeezes the card title/ref.
  const shortMap = {
    "Submitted":            "Submitted",
    "Under Review":         "Review",
    "Approved":             "Approved",
    "Ready for Collection": "Ready",
    "Collected":            "Collected",
    "Rejected":             "Rejected",
  };
  const s = map[status] || map["Submitted"];
  const shortLabel = shortMap[status] || status;
  return (
    <span className="text-[10px] font-extrabold uppercase tracking-wide rounded-full px-2.5 py-1 whitespace-nowrap shrink-0"
      style={{ background: s.bg, color: s.fg }}
      aria-label={`Status: ${status}`}>
      <span className="sm:hidden">{shortLabel}</span>
      <span className="hidden sm:inline">{status}</span>
    </span>
  );
}
